import React, { useState, useEffect } from 'react';
import { Search, ShoppingCart, Eye, Star, Truck, Shield, Award } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { productService } from '../services/productService';
import { notificationService } from '../services/notificationService';
import { Product } from '../types/product';
import ProductDetail from './ProductDetail';
import { PRODUCT_IMAGE_MAP, getOptimizedUrl } from '../generated-images-config';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const Products = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Función para obtener imagen de producto según categoría
  const getProductImage = (category: string, productImage: string): string => {
    // Primero intentar usar la imagen real del producto si existe
    if (productImage && productImage.trim() !== '') {
      return productImage;
    }
    
    // Si no hay imagen del producto, usar la imagen genérica de la categoría
    const categoryMap: { [key: string]: string } = {
      'spares': getOptimizedUrl(PRODUCT_IMAGE_MAP.spares),
      'machinery': getOptimizedUrl(PRODUCT_IMAGE_MAP.machinery),
      'tools': getOptimizedUrl(PRODUCT_IMAGE_MAP.tools),
      'safety': getOptimizedUrl(PRODUCT_IMAGE_MAP.safety),
      'automation': getOptimizedUrl(PRODUCT_IMAGE_MAP.automation),
      'catalog': getOptimizedUrl(PRODUCT_IMAGE_MAP.catalog)
    };
    
    // Si la categoría existe en nuestro mapa, usar la imagen generada
    if (categoryMap[category]) {
      return categoryMap[category];
    }
    
    // Si no, usar una imagen por defecto
    return 'https://via.placeholder.com/600x400?text=Producto+sin+imagen';
  };

  const categories = [
    { id: 'all', name: 'Todos', icon: '🔧' },
    { id: 'spares', name: 'Repuestos', icon: '⚙️' },
    { id: 'machinery', name: 'Maquinaria', icon: '🏭' },
    { id: 'tools', name: 'Herramientas', icon: '🛠️' },
    { id: 'safety', name: 'Seguridad', icon: '🦺' },
    { id: 'automation', name: 'Automatización', icon: '🤖' }
  ];

  useEffect(() => {
    const loadProducts = async () => {
      const allProducts = await productService.getProducts();
      setProducts(allProducts);
    };
    loadProducts();
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const features = [
    {
      icon: Truck,
      title: 'Entrega Rápida',
      description: 'Despacho en 24-48 horas para productos en stock'
    },
    {
      icon: Shield,
      title: 'Garantía Oficial',
      description: 'Todos nuestros productos tienen garantía del fabricante'
    },
    {
      icon: Award,
      title: 'Calidad Certificada',
      description: 'Productos con certificaciones internacionales'
    }
  ];

  const handleProductQuote = (productName: string, productId: number) => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
      // Pre-fill message in contact form
      setTimeout(() => {
        const messageTextarea = document.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
        if (messageTextarea) {
          messageTextarea.value = `Estoy interesado en cotizar el siguiente producto:\n\nProducto: ${productName}\nID: ${productId}\n\nPor favor, envíenme información sobre disponibilidad, precios y tiempo de entrega.`;
        }
      }, 500);
    }
  };

  const handleProductView = (product: Product) => {
    setSelectedProduct(product);
    setIsDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    setSelectedProduct(null);
  };

  const handleSpecialProductRequest = () => {
    // Navegar a la página de cotizaciones
    window.location.href = '/quote';
  };

  const handleDownloadCatalog = async () => {
    try {
      // Crear un nuevo documento PDF
      const pdf = new jsPDF();
      
      // Configurar fuentes y colores
      pdf.setFont('helvetica');
      
      // Agregar logo centrado en la parte superior
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          logoImg.src = '/logo.png'; // Ruta al logo principal
        });
        
        // Convertir logo a formato compatible con jsPDF
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = logoImg.width;
        canvas.height = logoImg.height;
        ctx?.drawImage(logoImg, 0, 0);
        
        const logoData = canvas.toDataURL('image/png', 1.0);
        
        // Calcular tamaño manteniendo el aspect ratio (reducido en 80% total: 60% + 20% adicional)
        const maxLogoHeight = 25 * 0.8; // 80% del tamaño original (60% + 20% adicional)
        const aspectRatio = logoImg.width / logoImg.height;
        const logoHeight = maxLogoHeight;
        const logoWidth = logoHeight * aspectRatio;
        
        // Posicionar centrado en la parte superior
        const logoXPos = (210 - logoWidth) / 2; // Centrado horizontalmente
        const logoYPos = 5; // 5mm del borde superior
        
        pdf.addImage(logoData, 'PNG', logoXPos, logoYPos, logoWidth, logoHeight);
        
      } catch (logoError) {
        console.warn('No se pudo cargar el logo:', logoError);
        // Si no se puede cargar el logo, continuar con el texto
      }
      
      // Título del catálogo (sin "Atomic Corporation")
      pdf.setFontSize(16);
      pdf.setTextColor(0, 51, 102); // Azul oscuro
      pdf.setFont('helvetica', 'bold');
      pdf.text('CATÁLOGO DE PRODUCTOS', 105, 35, { align: 'center' }); // Bajado de 30 a 35
      pdf.setFont('helvetica', 'normal'); // Restablecer fuente normal
      
      // Fecha de generación
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      const fechaActual = new Date().toLocaleDateString('es-PE');
      pdf.text(`Fecha de generación: ${fechaActual}`, 105, 45, { align: 'center' }); // Bajado de 40 a 45
      
      // Línea separadora
      pdf.setDrawColor(0, 51, 102);
      pdf.line(20, 50, 190, 50); // Bajada de 45 a 50
      
      // Productos
      let yPosition = 55; // Bajado de 50 a 55
      pdf.setFontSize(14);
      pdf.setTextColor(0, 51, 102);
      pdf.setFont('helvetica', 'normal');
      pdf.text('PRODUCTOS DISPONIBLES', 20, yPosition);
      
      yPosition += 15;
      
      // Usar los productos filtrados o todos los productos
      const productsToShow = filteredProducts.length > 0 ? filteredProducts : products;
      
      for (let index = 0; index < productsToShow.length; index++) {
        const product = productsToShow[index];
        
        // Calcular el espacio necesario para este producto antes de comenzar
        let estimatedHeight = 8; // Espacio para el nombre
        estimatedHeight += 8; // Espacio después del nombre
        
        // Intentar cargar la imagen para calcular su altura
        let imageLoaded = false;
        let imgHeight = 0;
        
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = getProductImage(product.category, product.image);
          });
          
          imgHeight = 45; // Altura fija de la imagen
          imageLoaded = true;
        } catch (imgError) {
          console.warn('No se pudo cargar la imagen del producto:', product.name, imgError);
        }
        
        // Calcular altura de la descripción
        const textWidth = imageLoaded ? 100 : 170;
        const descripcion = pdf.splitTextToSize(product.description, textWidth);
        const descriptionHeight = descripcion.length * 5;
        
        // Altura máxima entre descripción e imagen
        const maxContentHeight = Math.max(descriptionHeight, imgHeight);
        estimatedHeight += maxContentHeight + 8; // Espacio para contenido y separación
        
        // Espacio para precio, estado y rating
        estimatedHeight += 8; // Espacio para la línea de precio/estado/rating
        estimatedHeight += 15; // Espacio después de la línea de precio
        
        // Espacio para línea separadora (excepto el último producto)
        if (index < productsToShow.length - 1) {
          estimatedHeight += 10; // Espacio para línea separadora
        }
        
        // Verificar si necesitamos una nueva página antes de comenzar este producto
        if (yPosition + estimatedHeight > 270) { // 270 es un margen seguro antes del final de la página
          pdf.addPage();
          yPosition = 20;
        }
        
        // Nombre del producto
        pdf.setFontSize(12);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`${index + 1}. ${product.name}`, 20, yPosition);
        yPosition += 8;
        
        // Cargar y mostrar la imagen si está disponible
        if (imageLoaded) {
          try {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              img.src = getProductImage(product.category, product.image);
            });
            
            // Convertir imagen a formato compatible con jsPDF
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            
            const imgData = canvas.toDataURL('image/jpeg', 0.8);
            
            // Agregar imagen al lado derecho (tamaño 60x45 mm)
            const imgWidth = 60;
            const xPos = 130; // Posición fija en el lado derecho
            
            pdf.addImage(imgData, 'JPEG', xPos, yPosition, imgWidth, imgHeight);
            
          } catch (imgError) {
            console.warn('Error al agregar imagen al PDF:', product.name, imgError);
          }
        }
        
        // Descripción
        pdf.setFontSize(10);
        pdf.setTextColor(60, 60, 60);
        pdf.text(descripcion, 20, yPosition);
        
        // Calcular la posición Y después de la descripción
        yPosition += maxContentHeight + 8;
        
        // Precio y estado en la misma línea
        pdf.setFontSize(10);
        pdf.setTextColor(0, 51, 102);
        pdf.text(`Precio: $${product.price.toFixed(2)} + IGV`, 20, yPosition);
        
        if (product.inStock) {
          pdf.setTextColor(0, 128, 0); // Verde para productos en stock
        } else {
          pdf.setTextColor(255, 0, 0); // Rojo para productos agotados
        }
        pdf.text(`Estado: ${product.inStock ? 'En stock' : 'Agotado'}`, 80, yPosition);
        
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Rating: ${product.rating}/5 (${product.reviews} reseñas)`, 140, yPosition);
        yPosition += 15;
        
        // Línea separadora entre productos
        if (index < productsToShow.length - 1) {
          pdf.setDrawColor(200, 200, 200);
          pdf.line(20, yPosition, 190, yPosition);
          yPosition += 10;
        }
      }
      
      // Características
      if (yPosition > 220) {
        pdf.addPage();
        yPosition = 20;
      }
      
      yPosition += 10;
      pdf.setFontSize(14);
      pdf.setTextColor(0, 51, 102);
      pdf.setFont('helvetica', 'normal');
      pdf.text('CARACTERÍSTICAS', 20, yPosition);
      yPosition += 10;
      
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      features.forEach((feature) => {
        pdf.text(`• ${feature.title}: ${feature.description}`, 25, yPosition);
        yPosition += 6;
      });
      
      // Contacto
      yPosition += 10;
      pdf.setFontSize(14);
      pdf.setTextColor(0, 51, 102);
      pdf.setFont('helvetica', 'normal');
      pdf.text('CONTACTO', 20, yPosition);
      yPosition += 10;
      
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Teléfono: +51 953 759 419', 25, yPosition);
      yPosition += 6;
      pdf.text('Email: ventas@atomcorporations.com', 25, yPosition);
      yPosition += 6;
      pdf.text('Dirección: Av. Industrial 1234, Lima, Perú', 25, yPosition);
      
      // Pie de página con información de contacto
      const footerY = 297 - 10; // 10mm desde el borde inferior
      pdf.setFontSize(8); // Tamaño de fuente 8
      pdf.setTextColor(0, 0, 0); // Color negro para mejor legibilidad
      
      // Información de contacto separada por líneas verticales
      const emailText = 'cotizaciones@atomcorporations.com';
      const phoneText = '+51 953 759 419';
      const webText = 'www.atomcorporations.com';
      const separator = '|';
      
      // Calcular anchos para centrar
      const emailWidth = pdf.getTextWidth(emailText);
      const phoneWidth = pdf.getTextWidth(phoneText);
      const webWidth = pdf.getTextWidth(webText);
      const separatorWidth = pdf.getTextWidth(separator);
      
      // Espaciado entre elementos
      const spacing = 8;
      const totalWidth = emailWidth + separatorWidth + phoneWidth + separatorWidth + webWidth + (spacing * 4);
      const startX = (210 - totalWidth) / 2;
      
      // Posicionar cada elemento
      let currentX = startX;
      pdf.text(emailText, currentX, footerY);
      currentX += emailWidth + spacing;
      pdf.text(separator, currentX, footerY);
      currentX += separatorWidth + spacing;
      pdf.text(phoneText, currentX, footerY);
      currentX += phoneWidth + spacing;
      pdf.text(separator, currentX, footerY);
      currentX += separatorWidth + spacing;
      pdf.text(webText, currentX, footerY);
      
      // Pie de página existente (copyright)
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text('© 2024 Atomic Corporation - Todos los derechos reservados', 105, 280, { align: 'center' });
      
      // Descargar el PDF
      pdf.save('Atomic-Corporation-Catalogo-Productos.pdf');
      
    } catch (error) {
      console.error('Error al generar el catálogo PDF:', error);
      notificationService.error('Error al generar catálogo', 'Ocurrió un error al generar el catálogo. Por favor, inténtelo nuevamente.');
    }
  };

  return (
    <section id="products" className="section-padding bg-gray-50">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-6">
            Productos y Repuestos
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Catálogo de
            <span className="text-gradient"> Productos</span>
            {" "}Industriales
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Amplia variedad de productos y repuestos de alta calidad 
            para mantener sus operaciones funcionando sin interrupciones.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar productos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    selectedCategory === category.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="mr-2">{category.icon}</span>
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="card-hover group">
              <CardHeader className="p-0">
                <div className="relative">
                  <img
                    src={getProductImage(product.category, product.image)}
                    alt={product.name}
                    className="w-full h-48 object-cover rounded-t-xl cursor-pointer"
                    onClick={() => handleProductView(product)}
                  />
                  {product.featured && (
                    <div className="absolute top-4 left-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      DESTACADO
                    </div>
                  )}
                  {!product.inStock && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-t-xl flex items-center justify-center">
                      <div className="bg-red-500 text-white px-4 py-2 rounded-lg font-bold">
                        AGOTADO
                      </div>
                    </div>
                  )}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100"
                      onClick={() => handleProductView(product)}
                    >
                      <Eye className="w-5 h-5 text-gray-700" />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="mb-4">
                  <CardTitle className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                    {product.name}
                  </CardTitle>
                  <CardDescription className="text-gray-600 line-clamp-2">
                    {product.description}
                  </CardDescription>
                </div>
                
                <div className="flex items-center mb-4">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.floor(product.rating)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">
                    {product.rating} ({product.reviews})
                  </span>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-2xl font-bold text-primary-600">
                      ${product.price.toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-500">+ IGV</div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    className="flex-1 btn-primary"
                    disabled={!product.inStock}
                    onClick={() => handleProductQuote(product.name, product.id)}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    {product.inStock ? 'Cotizar' : 'No Disponible'}
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => handleProductView(product)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-primary-600 to-accent-500 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            ¿No encuentra lo que busca?
          </h3>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Tenemos acceso a más de 10,000 productos industriales. 
            Contáctenos y encontraremos exactamente lo que necesita.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 text-lg" onClick={handleSpecialProductRequest}>
              Solicitar Producto Especial
            </Button>
            <Button variant="outline" className="border-white text-white bg-transparent hover:bg-white hover:text-primary-600 px-8 py-4 text-lg" onClick={handleDownloadCatalog}>
              Descargar Catálogo Completo
            </Button>
          </div>
        </div>

        {/* Modal de detalles del producto */}
        <ProductDetail
          product={selectedProduct}
          isOpen={isDetailOpen}
          onClose={handleCloseDetail}
          onQuote={handleProductQuote}
        />
      </div>
    </section>
  );
};

export default Products;
